import {
  mapChangeSchema,
  mapPeriodsSchema,
  mapSliceSchema,
  regionTimeSeriesSchema,
  type MapChange,
  type MapChangeRequest,
  type MapPeriods,
  type MapPeriodsRequest,
  type MapSlice,
  type MapSliceRequest,
  type RegionTimeSeries,
  type RegionTimeSeriesRequest,
} from "@/features/explorer/api/contracts";

const mapSlicePath = "/api/national-data/map-slice";
const mapPeriodsPath = "/api/national-data/map-periods";
const mapChangePath = "/api/national-data/map-change";
const regionTimeSeriesPath = "/api/national-data/region-time-series";

export function buildMapSliceUrl(request: MapSliceRequest): string {
  const query = new URLSearchParams({
    geographyLevel: request.geographyLevel,
    metricId: request.metricId,
    period: request.period,
    statisticId: request.statisticId,
  });
  if (request.refresh) {
    query.set("refresh", "true");
  }
  return `${mapSlicePath}?${query.toString()}`;
}

function buildCommonUrl(path: string, params: Record<string, string>): string {
  const query = new URLSearchParams(params);
  return `${path}?${query.toString()}`;
}

export function buildMapPeriodsUrl(request: MapPeriodsRequest): string {
  const query = new URLSearchParams({
    geographyLevel: request.geographyLevel,
    metricId: request.metricId,
    statisticId: request.statisticId,
  });
  if (request.refresh) {
    query.set("refresh", "true");
  }
  return `${mapPeriodsPath}?${query.toString()}`;
}

export function buildMapChangeUrl(request: MapChangeRequest): string {
  const query = new URLSearchParams({
    baseline: request.baseline,
    geographyLevel: request.geographyLevel,
    metricId: request.metricId,
    period: request.period,
    statisticId: request.statisticId,
  });
  if (request.refresh) {
    query.set("refresh", "true");
  }
  return `${mapChangePath}?${query.toString()}`;
}

export function buildRegionTimeSeriesUrl(request: RegionTimeSeriesRequest): string {
  return buildCommonUrl(regionTimeSeriesPath, {
    geographyCodes: request.geographyCodes.join(","),
    geographyLevel: request.geographyLevel,
    metricId: request.metricId,
    statisticId: request.statisticId,
  });
}

async function fetchAndParse<T>(url: string, schema: { parse: (value: unknown) => T }, init: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    throw new Error(`Unable to load data (${response.status}).`);
  }
  return schema.parse(await response.json());
}

export function getMapSlice(request: MapSliceRequest, signal?: AbortSignal): Promise<MapSlice> {
  const init: RequestInit = {
    headers: { Accept: "application/json" },
    signal,
  };
  if (request.refresh) {
    init.cache = "no-cache";
  }
  return fetchAndParse(buildMapSliceUrl(request), mapSliceSchema, init);
}

export function getMapPeriods(request: MapPeriodsRequest, signal?: AbortSignal): Promise<MapPeriods> {
  const init: RequestInit = {
    headers: { Accept: "application/json" },
    signal,
  };
  if (request.refresh) {
    init.cache = "no-cache";
  }
  return fetchAndParse(buildMapPeriodsUrl(request), mapPeriodsSchema, init);
}

export function getMapChange(request: MapChangeRequest, signal?: AbortSignal): Promise<MapChange> {
  const init: RequestInit = {
    headers: { Accept: "application/json" },
    signal,
  };
  if (request.refresh) {
    init.cache = "no-cache";
  }
  return fetchAndParse(buildMapChangeUrl(request), mapChangeSchema, init);
}

export function getRegionTimeSeries(request: RegionTimeSeriesRequest, signal?: AbortSignal): Promise<RegionTimeSeries> {
  return fetchAndParse(buildRegionTimeSeriesUrl(request), regionTimeSeriesSchema, {
    headers: { Accept: "application/json" },
    signal,
  });
}
